import { useAuthStore } from '@/stores/authStore'
import { migrateLegacyAuthStorage } from '@/utils/authSessionStorage'
import { isAuthRefreshNoSession, postAuthRefresh } from '@/services/authRefresh'

let initPromise: Promise<void> | null = null

/**
 * Bootstrap de sessão (uma vez por carga da página).
 * Sempre define authReady no final — evita spinner infinito com Strict Mode.
 */
export function initAuthSession(): Promise<void> {
  if (initPromise) return initPromise

  initPromise = (async () => {
    migrateLegacyAuthStorage()

    const { token, login, logout, markAuthSessionReady } = useAuthStore.getState()
    const forceReadyTimer = window.setTimeout(() => {
      if (!useAuthStore.getState().authReady) markAuthSessionReady()
    }, 10_000)

    try {
      if (token) return

      // Usuário clicou em Sair — cookies já foram limpos no logout(); só evita refresh.
      try {
        if (sessionStorage.getItem('crm_explicit_logout') === '1') {
          sessionStorage.removeItem('crm_explicit_logout')
          return
        }
      } catch {
        /* ignore */
      }

      if (import.meta.env.DEV) {
        const e2e = (window as Window & {
          __E2E_AUTH__?: { user: Parameters<typeof login>[0]; token: string }
        }).__E2E_AUTH__
        if (e2e?.token) {
          login(e2e.user, e2e.token)
          return
        }
      }

      try {
        const resp = await postAuthRefresh()
        if (isAuthRefreshNoSession(resp.status)) return
        const result = resp.data?.data
        if (result?.user) {
          login(result.user, result.token ?? '')
        }
      } catch (err: unknown) {
        const axiosErr = err as {
          response?: { status?: number; data?: { error?: { code?: string } } }
        }
        const status = axiosErr.response?.status
        const code = axiosErr.response?.data?.error?.code
        // Compat: servidores antigos retornam 400/401 MISSING_TOKEN no bootstrap.
        if ((status === 400 || status === 401) && code === 'MISSING_TOKEN') return
        await logout()
      }
    } finally {
      window.clearTimeout(forceReadyTimer)
      if (!useAuthStore.getState().authReady) {
        markAuthSessionReady()
      }
    }
  })()

  return initPromise
}

/** Aguarda reidratação do persist antes do bootstrap */
export function initAuthSessionAfterHydration(): Promise<void> {
  if (useAuthStore.persist.hasHydrated()) {
    return initAuthSession()
  }

  return new Promise((resolve) => {
    let settled = false
    let unsub: (() => void) | undefined

    const finish = () => {
      if (settled) return
      settled = true
      unsub?.()
      window.clearTimeout(hydrationFallback)
      void initAuthSession().then(resolve)
    }

    unsub = useAuthStore.persist.onFinishHydration(finish)
    const hydrationFallback = window.setTimeout(finish, 2_500)
  })
}
